import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Edit, TrendingUp, Upload, Download } from "lucide-react";
import CsvImportDialog from "../../components/CsvImportDialog";
import { exportCsv } from "../../lib/csvExport";

const EMPTY = { name:"", from_place:"", to_place:"", stops:[], distance_km:0, travel_time_min:0, departure_time:"06:00", arrival_time:"", operating_days:["Mon","Tue","Wed","Thu","Fri","Sat","Sun"], vehicle_type:"Tempo Traveller", ac:true, seat_capacity:17, price_per_seat:800, booking_cutoff_min:60, active:true };

const CSV_SCHEMA = [
  { key: "name",             header: "name",             type: "str", required: true },
  { key: "from_place",       header: "from_place",       type: "str", required: true },
  { key: "to_place",         header: "to_place",         type: "str", required: true },
  { key: "stops",            header: "stops",            type: "list", default: [] },
  { key: "distance_km",      header: "distance_km",      type: "num", default: 0 },
  { key: "travel_time_min",  header: "travel_time_min",  type: "int", default: 0 },
  { key: "departure_time",   header: "departure_time",   type: "str", required: true },
  { key: "arrival_time",     header: "arrival_time",     type: "str" },
  { key: "vehicle_type",     header: "vehicle_type",     type: "str", required: true },
  { key: "ac",               header: "ac",               type: "bool", default: true },
  { key: "seat_capacity",    header: "seat_capacity",    type: "int", required: true },
  { key: "price_per_seat",   header: "price_per_seat",   type: "num", required: true },
  { key: "booking_cutoff_min", header: "booking_cutoff_min", type: "int", default: 60 },
  { key: "active",           header: "active",           type: "bool", default: true },
];
const CSV_SAMPLE = { name:"Palus → Pune Morning", from_place:"Palus", to_place:"Pune", stops:["Islampur","Karad","Satara"], distance_km:280, travel_time_min:360, departure_time:"06:00", arrival_time:"12:00", vehicle_type:"Tempo Traveller", ac:true, seat_capacity:17, price_per_seat:700, booking_cutoff_min:60, active:true };

export default function AdminRoutes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRates, setBulkRates] = useState({});
  const [csvOpen, setCsvOpen] = useState(false);

  const load = () => api.get("/routes/all").then(r=>setItems(r.data));
  useEffect(()=>{ load().catch(()=>{}); }, []);

  const save = async () => {
    try {
      const payload = {...form, distance_km: parseFloat(form.distance_km)||0, travel_time_min: parseInt(form.travel_time_min)||0, seat_capacity: parseInt(form.seat_capacity), price_per_seat: parseFloat(form.price_per_seat), booking_cutoff_min: parseInt(form.booking_cutoff_min)||60, stops: typeof form.stops==="string"? form.stops.split(",").map(s=>s.trim()).filter(Boolean): form.stops };
      if (editing) await api.put(`/routes/${editing}`, {...payload, id: editing});
      else await api.post("/routes", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete route?")) return; await api.delete(`/routes/${id}`); load(); toast.success("Deleted"); };
  const edit = (r) => { setEditing(r.id); setForm({...r, stops: (r.stops||[]).join(", ")}); setOpen(true); };

  const openBulk = () => {
    const map = {}; items.forEach(r => { map[r.id] = r.price_per_seat; });
    setBulkRates(map); setBulkOpen(true);
  };
  const saveBulk = async () => {
    const updates = items
      .filter(r => Number(bulkRates[r.id]) !== Number(r.price_per_seat))
      .map(r => ({ id: r.id, price_per_seat: parseFloat(bulkRates[r.id]) || 0 }));
    if (updates.length === 0) { toast.info("No changes"); return; }
    try {
      const res = await api.patch("/routes/bulk-rates", { updates });
      toast.success(`Updated ${res.data.updated} routes`); setBulkOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-3xl font-display font-black tracking-tight">Daily Routes</h1>
        <div className="flex gap-2">
          <Button onClick={()=>exportCsv({filename:"routes",headers:CSV_SCHEMA,rows:items})} variant="outline" data-testid="rt-export-csv-btn" disabled={items.length===0}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button onClick={()=>setCsvOpen(true)} variant="outline" data-testid="rt-import-csv-btn"><Upload className="w-4 h-4 mr-1" /> Import CSV</Button>
          <Button onClick={openBulk} variant="outline" data-testid="bulk-rate-btn"><TrendingUp className="w-4 h-4 mr-1" /> Bulk Rate Edit</Button>
          <Button onClick={()=>{setEditing(null);setForm(EMPTY);setOpen(true);}} className="bg-[#ea580c] hover:bg-orange-700" data-testid="add-route-btn"><Plus className="w-4 h-4 mr-1" /> Add Route</Button>
        </div>
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {items.map(r => (
          <div key={r.id} className="card-lift p-5 rounded-xl bg-white border border-slate-200" data-testid={`route-item-${r.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-display font-bold text-lg">{r.name}</div><div className="text-sm text-slate-500">{r.from_place} → {r.to_place}</div></div>
              <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{r.active?"Active":"Inactive"}</div>
            </div>
            <div className="mt-3 text-sm text-slate-600">Dep {r.departure_time} · {r.vehicle_type} · {r.seat_capacity}s · {r.ac?"AC":"Non-AC"}</div>
            <div className="mt-2 text-lg font-black text-[#1e1b4b]">₹{r.price_per_seat}<span className="text-xs font-normal text-slate-500">/seat</span></div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={()=>edit(r)} data-testid={`edit-route-${r.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={()=>del(r.id)} data-testid={`delete-route-${r.id}`}><Trash2 className="w-3 h-3 mr-1 text-red-500" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Route Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?"Edit":"Add"} Route</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="rt-name" /></div>
            <div><Label>From</Label><Input value={form.from_place} onChange={e=>setForm({...form,from_place:e.target.value})} data-testid="rt-from" /></div>
            <div><Label>To</Label><Input value={form.to_place} onChange={e=>setForm({...form,to_place:e.target.value})} data-testid="rt-to" /></div>
            <div className="md:col-span-2"><Label>Stops (comma separated)</Label><Input value={typeof form.stops==='string'?form.stops:(form.stops||[]).join(', ')} onChange={e=>setForm({...form,stops:e.target.value})} /></div>
            <div><Label>Distance km</Label><Input type="number" value={form.distance_km} onChange={e=>setForm({...form,distance_km:e.target.value})} /></div>
            <div><Label>Travel Time (min)</Label><Input type="number" value={form.travel_time_min} onChange={e=>setForm({...form,travel_time_min:e.target.value})} /></div>
            <div><Label>Departure Time</Label><Input type="time" value={form.departure_time} onChange={e=>setForm({...form,departure_time:e.target.value})} /></div>
            <div><Label>Arrival Time</Label><Input type="time" value={form.arrival_time||""} onChange={e=>setForm({...form,arrival_time:e.target.value})} /></div>
            <div><Label>Vehicle Type (free text)</Label><Input value={form.vehicle_type} onChange={e=>setForm({...form,vehicle_type:e.target.value})} placeholder="Tempo Traveller, Ertiga, Bus…" data-testid="rt-vehicle-type" /></div>
            <div><Label>Seats</Label><Input type="number" value={form.seat_capacity} onChange={e=>setForm({...form,seat_capacity:e.target.value})} /></div>
            <div><Label>Price / Seat ₹</Label><Input type="number" value={form.price_per_seat} onChange={e=>setForm({...form,price_per_seat:e.target.value})} /></div>
            <div><Label>Cutoff (min)</Label><Input type="number" value={form.booking_cutoff_min} onChange={e=>setForm({...form,booking_cutoff_min:e.target.value})} /></div>
            <div className="flex items-end gap-2"><Label>AC</Label><Switch checked={form.ac} onCheckedChange={v=>setForm({...form,ac:v})} /></div>
            <div className="flex items-end gap-2"><Label>Active</Label><Switch checked={form.active} onCheckedChange={v=>setForm({...form,active:v})} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#ea580c] hover:bg-orange-700" data-testid="rt-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Rate Edit Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bulk Rate Edit</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Update seat prices for many routes in one go — perfect when diesel or toll rates move.</p>
          <div className="mt-2 divide-y">
            {items.map(r => (
              <div key={r.id} className="py-2 flex items-center justify-between gap-3" data-testid={`bulk-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.from_place} → {r.to_place} · {r.departure_time}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-400 line-through">₹{r.price_per_seat}</span>
                  <Input type="number" value={bulkRates[r.id] ?? r.price_per_seat} onChange={e=>setBulkRates({...bulkRates, [r.id]: e.target.value})} className="w-24" data-testid={`bulk-input-${r.id}`} />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={saveBulk} className="bg-[#ea580c] hover:bg-orange-700" data-testid="bulk-save">Save All Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Routes"
        endpoint="/routes/bulk-import"
        schema={CSV_SCHEMA}
        sampleRow={CSV_SAMPLE}
        onImported={load}
      />
    </div>
  );
}
